<template>
  <div>
    <ContentSectionLoader v-if="!loaded" />
    <Transition name="fade">
      <div v-if="loaded">
        <div
          v-if="membership.attributes.deactivated"
          class="card text-center bg-white max-w-sm mx-auto p-8"
        >
          <h1 v-text="'Alert'" />
          <p v-text="'This membership has been deleted.'" />
        </div>
        <div
          v-else
          class="text-center"
        >
          <img
            :src="membership.attributes.small_avatar_url"
            alt=""
            class="rounded-full w-24 h-24"
          >
          <h2>
            {{ membership.attributes.full_name }}
            <span v-if="membership.attributes.email">
              -
              <a
                :href="`mailto:${membership.attributes.email}`"
                v-text="membership.attributes.email"
              />
            </span>
          </h2>
          <div
            v-if="membership.membershipTitles.data.length"
            class="flex flex-col justify-center"
          >
            <MembershipTitle
              v-for="title in membership.membershipTitles.data"
              :key="title.label"
              class="m-0"
              :membership-title="title"
            />
          </div>
          <ul class="list-reset">
            <li>
              <strong v-text="'Membership status:'" /> {{ status }}
            </li>
            <li>
              <strong v-text="'Work status:'" /> {{ workStatus }}
            </li>
            <li v-if="nextPaymentDueDate">
              <strong v-text="'Next Payment Due:'"/> {{ nextPaymentDueDate }}
            </li>
          </ul>
          <div
            v-if="canGroupAdminEditMembershipProfile"
            class="max-w-xs mx-auto flex items-baseline justify-center my-6"
          >
            <a
              :href="editUrl"
              class="btn btn--sm btn--warning"
              v-text="'Edit'"
            />
          </div>
        </div>
        <section
          v-if="!membership.attributes.deactivated"
          class="flex flex-wrap items-stretch pb-8 border-b border-grey"
        >
          <div class="w-fill md:w-1/3">
            <h3
              class="font-bold"
              v-text="'Phone:'"
            />
            <div v-if="hasAnyVisiblePhoneNumbers">
              <div
                is="PhoneNumber"
                v-for="phoneNumber in membership.phoneNumbers.data"
                :key="phoneNumber.uuid"
                :phone-number="phoneNumber"
              />
            </div>
            <p
              v-else
              class="opacity-50"
              v-text="'None available'"
            />
          </div>
          <div class="w-fill md:w-1/3">
            <h3
              class="font-bold"
              v-text="'Address:'"
            />
            <div v-if="hasAnyVisibleAddresses">
              <div
                is="Address"
                v-for="address in membership.addresses.data"
                :key="address.uuid"
                :address="address"
              />
            </div>
            <p
              v-else
              class="opacity-50"
              v-text="'None available'"
            />
          </div>
          <div class="w-fill md:w-1/3">
            <h3
              class="font-bold"
              v-text="'Emergency Contacts:'"
            />
            <div v-if="hasAnyVisibleEmergencyContacts">
              <div
                is="EmergencyContact"
                v-for="emergencyContact in membership.emergencyContacts.data"
                :key="emergencyContact.uuid"
                :emergency-contact="emergencyContact"
              />
            </div>
            <p
              v-else
              class="opacity-50"
              v-text="'None available'"
            />
          </div>
        </section>
        <h4
          v-if="membership.membershipGroups.data.length"
          class="font-bold mt-8 text-2xl text-center opacity-50-darker"
          v-text="'Member Groups'"
        />
        <section class="flex flex-wrap items-stretch justify-center">
          <div
            is="MembershipGroup"
            v-for="membershipGroup in membership.membershipGroups.data"
            :key="membershipGroup.uuid"
            :membership-group="membershipGroup"
            :show-remove-action="false"
          />
        </section>
      </div>
    </Transition>
  </div>
</template>

<script type="text/babel">
import { mapState, mapActions } from 'vuex'
import Address from 'Membership/Resources/assets/js/components/_partials/Address'
import PhoneNumber from 'Membership/Resources/assets/js/components/_partials/PhoneNumber'
import MembershipGroup from 'Membership/Resources/assets/js/components/_partials/MembershipGroup'
import EmergencyContact from 'Membership/Resources/assets/js/components/_partials/EmergencyContact'
import MembershipTitle from 'Membership/Resources/assets/js/components/_partials/MembershipTitle'

export default {
  name: 'MembershipDetails',
  components: {
    Address,
    PhoneNumber,
    MembershipGroup,
    EmergencyContact,
    MembershipTitle,
  },
  props: {
    membershipUuid: {
      type: String,
      required: true,
    },
    canGroupAdminEditMembershipProfile: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      loaded: false,
    }
  },
  computed: {
    ...mapState({
      membership: state => state.membership.membership,
    }),
    editUrl() {
      return this.$urlGenerator.active(`members/${this.membership.uuid}/edit`)
    },
    hasAnyVisibleAddresses() {
      return this.membership.addresses.data.length > 0
    },
    hasAnyVisiblePhoneNumbers() {
      return _.filter(this.membership.phoneNumbers.data, 'attributes.phone_number').length
    },
    hasAnyVisibleEmergencyContacts() {
      return _.filter(this.membership.emergencyContacts.data, 'attributes.full_name').length
    },
    status() {
      return _.upperFirst(this.membership.attributes.status)
    },
    workStatus() {
      let workStatus = this.membership.attributes.work_status

      const statusesToAlter = {
        active: 'active_member',
        auxiliary: 'auxiliary_member',
        retired: 'retired_member',
      }

      if (statusesToAlter.hasOwnProperty(this.membership.attributes.work_status)) {
        workStatus = statusesToAlter[this.membership.attributes.work_status]
      }

      return _.startCase(workStatus.replace('_', ' '))
    },
    nextPaymentDueDate() {
      return this.membership.subscription?.attributes
        ? this.$date.toMomentDateFormat(this.membership.subscription.attributes.current_period_end * 1000)
        : null
    },
  },
  async mounted() {
    await this.$nextTick()

    this.fetch({
      membershipUuid: this.membershipUuid,
      params: {
        include: 'addresses,emergencyContacts,phoneNumbers,membershipGroups,union,membershipTitles,subscription',
      },
    }).then(() => {
      this.loaded = true
    })
  },
  methods: {
    ...mapActions({
      fetch: 'membershipGetMembership',
    }),
  },
}
</script>
